import { api } from '@goaltree/backend/convex/_generated/api'
import {
  COACH_STYLES,
  COACH_VOICES,
  type CoachStyle,
  type CoachVoice,
  onboardingCoachSuggestions,
  onboardingCoachVoice,
  realisticCoach,
  realisticVoice,
} from '@goaltree/backend/convex/lib/coach'
import type { Focus } from '@goaltree/backend/convex/lib/focus'
import type { HeardFrom } from '@goaltree/backend/convex/lib/heardFrom'
import {
  type SuggestionKey,
  areaForCategory,
  cleanImproveAreas,
  defaultSuggestionPicks,
} from '@goaltree/backend/convex/lib/improveAreas'
import {
  NIGHT_OWL_DAY_START_HOUR,
  ONBOARDING_COACHES,
  dayStartHourFor,
  defaultHabitPicks,
  suggestHabits,
} from '@goaltree/backend/convex/lib/onboardingFlow'
import { cn } from '@goaltree/ui/lib/utils'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import { Bell, Check, ChevronRight, Loader2, Lock, Moon } from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CoachAvatar, RealisticCoachAvatar } from '@/components/coach-avatar'
import { HeardFromPicker } from '@/components/heard-from-picker'
import { habitColor } from '@/components/inbox-habits'
import {
  ImproveAreasStep,
  ImproveSubAreasStep,
  ImproveSuggestStep,
} from '@/components/onboarding-improve'
import { OnboardingHeader } from '@/components/onboarding-progress'
import { FloatingFooter } from '@/components/plan-review'
import { ACCENT_OPTIONS, useAccentTheme } from '@/hooks/use-accent-theme'
import { useIntroPreview } from '@/hooks/use-intro-preview'
import { useIsPro } from '@/hooks/use-is-pro'
import { useOnboardingMode } from '@/hooks/use-onboarding-mode'
import { usePaywallVariant } from '@/hooks/use-paywall-variant'
import { useUpdatePreferences } from '@/hooks/use-update-preferences'
import { useWebPush } from '@/hooks/use-web-push'
import { authClient } from '@/lib/auth-client'
import { useCategoryFill } from '@/lib/category-color'
import { clearCoachStyleHandoff, readCoachStyleHandoff } from '@/lib/coach-style-handoff'
import { markOnboardingFinished } from '@/lib/first-run'
import { POPULAR_HABITS, POPULAR_HABIT_CATEGORIES, popularMeasure } from '@/lib/popular-habits'
import { posthog } from '@/lib/posthog'
import { handleUsageLimit, openHabitUpsell } from '@/lib/usage-limit'
import { useOnboardingStore } from '@/store/onboarding.store'
import { useViewStore } from '@/store/view.store'

// Onboarding steps 4–5 (habits, coach & reminders), after the plan is saved —
// or straight here when a signed-in newcomer skips the goal.
export const Route = createFileRoute('/_authenticated/onboarding/user-profile')({
  validateSearch: (search: Record<string, unknown>): { goalId?: string } => ({
    goalId: typeof search.goalId === 'string' ? search.goalId : undefined,
  }),
  component: OnboardingSetup,
})

function ChipButton({
  label,
  selected,
  onClick,
}: {
  label: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`cursor-pointer rounded-full border px-4 py-2 text-sm font-medium transition-all ${
        selected
          ? 'border-primary/70 bg-primary/10 text-primary-text'
          : 'border-border/60 bg-card/40 text-muted-foreground hover:border-border hover:text-foreground'
      }`}
    >
      {label}
    </button>
  )
}

const primaryBtn =
  'cursor-pointer flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3.5 text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 hover:scale-[1.01] active:scale-[0.99] disabled:cursor-wait disabled:scale-100'

const linkBtn =
  'cursor-pointer py-1 text-center text-sm text-muted-foreground transition-colors hover:text-foreground'

type Prefs = {
  dayStartHour?: number
  coachVoice?: string
  coachStyle?: string
  notifStreakAlerts?: boolean
}

// Step 5's choices, held above the steps so they survive going back to habits.
// Unset fields fall back to the saved prefs / login name.
type CoachDraft = {
  name?: string
  voice?: string
  style?: CoachStyle | null
  nightOwl?: boolean
  remind?: boolean
  heardFrom?: HeardFrom
  heardFromOther?: string
}

const FEATURED = new Set<string>(ONBOARDING_COACHES.map((c) => c.voice))
const MORE_VOICES = COACH_VOICES.filter((v) => !FEATURED.has(v.id))

type HabitStage = 'areas' | 'subAreas' | 'suggest' | 'habits'

// Step 4 sub-steps fill the progress segment; Skip on the area steps falls
// back to the popular-habits list.
const STAGE_PARTIAL: Record<HabitStage, number> = {
  areas: 0.25,
  subAreas: 0.5,
  suggest: 0.75,
  habits: 0.75,
}
const STAGE_BACK: Partial<Record<HabitStage, HabitStage>> = {
  subAreas: 'areas',
  suggest: 'subAreas',
  habits: 'areas',
}

function OnboardingSetup() {
  const { goalId } = Route.useSearch()
  const goals = useQuery(api.nodes.getUserGoals)
  const pendingTree = useOnboardingStore((s) => s.generatedTree)
  const goal = goals?.find((g) => g._id === goalId)
  const [step, setStep] = useState<HabitStage | 'coach'>('areas')
  const [habitStage, setHabitStage] = useState<HabitStage>('areas')
  const onboarding = useOnboardingMode()
  const [picked, setPicked] = useState<Set<string> | null>(null)
  const [added, setAdded] = useState<Set<string>>(() => new Set())
  const [draft, setDraft] = useState<CoachDraft>(() => ({ style: readCoachStyleHandoff() }))
  const category = goal?.category ?? pendingTree?.category
  // Null until touched, so the goal's area is pre-selected once it loads.
  const [areaPick, setAreaPick] = useState<string[] | null>(null)
  const goalArea = areaForCategory(category)
  const areas = areaPick ?? (goalArea ? [goalArea] : [])
  const [subAreas, setSubAreas] = useState<string[]>([])
  const [suggestPicks, setSuggestPicks] = useState<{ for: string; keys: SuggestionKey[] } | null>(
    null,
  )
  const [suggestAdded, setSuggestAdded] = useState<Set<SuggestionKey>>(() => new Set())
  const [areasSaved, setAreasSaved] = useState(false)
  const setImproveAreas = useMutation(api.preferences.setImproveAreas)

  const go = (s: HabitStage | 'coach') => {
    if (s !== 'coach') setHabitStage(s)
    setStep(s)
  }
  const navigate = useNavigate()
  // Habits-only first step: back returns to "What brings you here?".
  const toFocus =
    !goalId && onboarding.focus === 'habits'
      ? () => {
          useOnboardingStore.getState().setFocus(null)
          void navigate({ to: '/goals/new' })
        }
      : undefined
  const back =
    step === 'coach'
      ? () => setStep(habitStage)
      : STAGE_BACK[step]
        ? () => go(STAGE_BACK[step]!)
        : toFocus

  function toSuggestions() {
    const clean = cleanImproveAreas(areas, subAreas)
    setSubAreas(clean.subAreas)
    // Re-tick the defaults only when the sub-areas changed since the last visit.
    const key = clean.subAreas.join()
    setSuggestPicks((prev) =>
      prev?.for === key ? prev : { for: key, keys: defaultSuggestionPicks(clean.subAreas) },
    )
    setAreasSaved(true)
    void setImproveAreas(clean).catch(() => {})
    go('suggest')
  }

  return (
    <div className="relative flex h-svh flex-col overflow-hidden bg-background">
      <OnboardingHeader
        step={onboarding.step + (step === 'coach' ? 1 : 0)}
        partial={step === 'coach' ? undefined : STAGE_PARTIAL[step]}
        total={onboarding.total}
        onBack={back}
      />
      {step === 'areas' ? (
        <ImproveAreasStep
          planSaved={!!goalId}
          areas={areas}
          setAreas={setAreaPick}
          onNext={() => go('subAreas')}
          onSkip={() => go('habits')}
        />
      ) : step === 'subAreas' ? (
        <ImproveSubAreasStep
          areas={areas}
          subAreas={subAreas}
          setSubAreas={setSubAreas}
          onNext={toSuggestions}
          onSkip={() => go('habits')}
        />
      ) : step === 'suggest' ? (
        <ImproveSuggestStep
          subAreas={subAreas}
          picked={suggestPicks?.keys ?? []}
          setPicked={(keys) => setSuggestPicks((prev) => ({ for: prev?.for ?? '', keys }))}
          added={suggestAdded}
          setAdded={setSuggestAdded}
          onDone={() => go('coach')}
        />
      ) : step === 'habits' ? (
        <HabitsStep
          goalTitle={goal?.title ?? pendingTree?.title}
          planSaved={!!goalId}
          category={category}
          picked={picked}
          setPicked={setPicked}
          added={added}
          setAdded={setAdded}
          onDone={() => go('coach')}
        />
      ) : (
        <CoachStep
          goalId={goalId}
          focus={onboarding.focus}
          category={areasSaved ? undefined : category}
          draft={draft}
          update={(d) => setDraft((prev) => ({ ...prev, ...d }))}
        />
      )}
    </div>
  )
}

function HabitsStep({
  goalTitle,
  planSaved,
  category,
  picked,
  setPicked,
  added,
  setAdded,
  onDone,
}: {
  goalTitle?: string
  planSaved: boolean
  category?: string
  // Null until the user touches a row, so the pre-ticks follow the goal's
  // category once it loads.
  picked: Set<string> | null
  setPicked: (s: Set<string>) => void
  // Already created on an earlier pass; locked so going back can't duplicate them.
  added: Set<string>
  setAdded: (s: Set<string>) => void
  onDone: () => void
}) {
  const { t } = useTranslation()
  const fill = useCategoryFill()
  const addHabits = useMutation(api.nodes.addHabits)
  const existing = useQuery(api.nodes.getHabits)
  const have = new Set(existing?.map((h) => h.title.split('///')[0].trim().toLowerCase()))
  // Habits created on an earlier pass stay listed (locked); others already tracked are hidden.
  const pool = POPULAR_HABITS.filter(
    (h) => added.has(h.key) || !have.has(t(`inbox.popularHabits.${h.key}`).toLowerCase()),
  )
  const defaults = defaultHabitPicks(pool.filter((h) => !added.has(h.key)))
  const suggestions = [
    ...defaults,
    ...suggestHabits(pool, category).filter((h) => !defaults.includes(h)),
  ]
  const selected = picked ?? new Set(defaults.map((h) => h.key))
  const fresh = [...selected].flatMap((k) => pool.filter((h) => h.key === k && !added.has(k)))
  // Free plan: picks past the remaining allowance are shown as Pro and not added.
  const allowance = useQuery(api.nodes.getMyHabitAllowance)
  const room = allowance?.limit == null ? Infinity : Math.max(0, allowance.limit - allowance.used)
  const addable = fresh.slice(0, room)
  const proKeys = new Set(fresh.slice(room).map((h) => h.key))
  // 'all' keeps the preticked popular habits in view; tapping a chip filters.
  const [catPick, setCatPick] = useState<string | null>(null)
  const cat = catPick ?? 'all'
  const suggestedKeys = new Set(suggestions.map((h) => h.key))
  const shown =
    cat === 'all'
      ? [...suggestions, ...pool.filter((h) => !suggestedKeys.has(h.key))]
      : pool.filter((h) => h.category === cat)
  const [saving, setSaving] = useState(false)

  function toggle(key: string) {
    if (added.has(key)) return
    const next = new Set(selected)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    setPicked(next)
  }

  async function handleAdd() {
    setSaving(true)
    const habits = addable.map((h) => ({
      title: t(`inbox.popularHabits.${h.key}`),
      description: t(`inbox.popularHabitDesc.${h.key}`, { defaultValue: '' }) || undefined,
      category: h.category,
      icon: h.icon,
      recursPeriod: h.cadence,
      measure: popularMeasure(t, h),
    }))
    const ok = await addHabits({ habits, recurrenceTzOffset: new Date().getTimezoneOffset() })
      .then(() => true)
      .catch((err) => {
        handleUsageLimit(err)
        return false
      })
    if (ok) {
      setPicked(selected)
      setAdded(new Set([...added, ...addable.map((h) => h.key)]))
    }
    setSaving(false)
    onDone()
  }

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-lg space-y-6 px-4 pt-6 pb-48">
          <div className="space-y-1">
            {planSaved && (
              <p className="text-xs font-medium text-primary-text">
                {t('onboarding.flow.planSaved')}
              </p>
            )}
            <h1 className="text-2xl font-black tracking-tight">
              {t('onboarding.flow.habitsTitle')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {goalTitle
                ? t('onboarding.flow.habitsHint', { goal: goalTitle })
                : t('onboarding.userProfile.habitsHint')}
            </p>
          </div>
          <div className="sticky top-0 z-10 -mx-4 overflow-x-auto bg-background/95 px-4 py-2 backdrop-blur [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex w-max gap-1.5">
              {(['all', ...POPULAR_HABIT_CATEGORIES] as const).map((c) => {
                const active = c === cat
                return (
                  <button
                    key={c}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setCatPick(c)}
                    className={cn(
                      'flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                      active
                        ? 'border-primary/50 bg-primary/10 text-primary-text'
                        : 'border-border text-foreground hover:bg-muted',
                    )}
                  >
                    {c !== 'all' && (
                      <span
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ background: fill(habitColor(c)) }}
                      />
                    )}
                    {c === 'all' ? t('common.all') : t(`inbox.habitCategory.${c}`)}
                  </button>
                )
              })}
            </div>
          </div>
          <div className="space-y-2">
            {shown.map((h) => {
              const on = selected.has(h.key)
              return (
                <button
                  key={h.key}
                  type="button"
                  onClick={() => toggle(h.key)}
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-all',
                    on
                      ? 'border-primary/70 bg-primary/10'
                      : 'border-border/60 bg-card/40 hover:border-border',
                    added.has(h.key) && 'cursor-default',
                  )}
                >
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ background: fill(habitColor(h.category)) }}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {t(`inbox.popularHabits.${h.key}`)}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {t(`inbox.popularHabitDesc.${h.key}`)} ·{' '}
                      {t(`inbox.period.${h.cadence}`).toLowerCase()}
                    </p>
                  </div>
                  {proKeys.has(h.key) && (
                    <span className="shrink-0 rounded-full bg-primary/12 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-text">
                      {t('habitLimit.proBadge')}
                    </span>
                  )}
                  <span
                    className={cn(
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                      on ? 'border-primary bg-primary' : 'border-border/50',
                    )}
                  >
                    {on && <Check className="h-3 w-3 text-primary-foreground" strokeWidth={3} />}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      </div>
      <FloatingFooter>
        {proKeys.size > 0 && (
          <div className="flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/8 px-3 py-2 text-xs text-foreground">
            <span className="min-w-0 flex-1">
              {t('habitLimit.heldBackDesc', { limit: allowance?.limit ?? 3 })}
            </span>
            <button
              type="button"
              onClick={openHabitUpsell}
              className="shrink-0 cursor-pointer font-semibold text-primary-text hover:underline"
            >
              {t('habitLimit.upgrade')}
            </button>
          </div>
        )}
        <button
          type="button"
          disabled={saving}
          onClick={addable.length ? handleAdd : onDone}
          className={primaryBtn}
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          {addable.length
            ? t('onboarding.flow.addHabits', { count: addable.length })
            : t('onboarding.userProfile.next')}
        </button>
        <button type="button" disabled={saving} onClick={onDone} className={linkBtn}>
          {t('onboarding.userProfile.skip')}
        </button>
      </FloatingFooter>
    </div>
  )
}

function CoachCard({
  voice,
  label,
  blurb,
  image,
  selected,
  playing,
  trialPill,
  onSelect,
  className,
}: {
  voice: string
  label: string
  blurb: string
  image?: string
  selected: boolean
  playing: boolean
  trialPill: boolean
  onSelect: () => void
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <div className={cn('relative', className)}>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className={cn(
          'flex h-full w-full cursor-pointer flex-col items-center gap-1.5 rounded-2xl border px-2 py-3 transition-all',
          selected
            ? 'border-primary/70 bg-primary/10'
            : 'border-border/60 bg-card/40 hover:border-border',
        )}
      >
        {image ? (
          <RealisticCoachAvatar image={image} size={40} speaking={playing} />
        ) : (
          <CoachAvatar voice={voice} size={40} speaking={playing} />
        )}
        <span className="text-sm font-bold text-foreground">{label}</span>
        <span className="text-xs text-muted-foreground">{blurb}</span>
        {trialPill && (
          <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap text-primary-text">
            {t('onboarding.flow.trialVoicePill')}
          </span>
        )}
      </button>
    </div>
  )
}

// Pro colours show locked; tapping one says so instead of opening the paywall
// mid-onboarding. The choice applies instantly (stored per device).
function AccentPicker() {
  const { t } = useTranslation()
  const fill = useCategoryFill()
  const { accent, setAccent } = useAccentTheme()
  const { isPro } = useIsPro()
  const [lockedTap, setLockedTap] = useState(false)
  return (
    <div className="space-y-2.5">
      <p className="text-sm font-semibold text-foreground/70">{t('onboarding.flow.colorLabel')}</p>
      <div className="flex flex-wrap items-center gap-2.5">
        {ACCENT_OPTIONS.map((opt) => {
          const active = accent === opt.id
          const locked = opt.pro && !isPro
          return (
            <button
              key={opt.id}
              type="button"
              aria-pressed={active}
              aria-label={opt.category ? t(`goals.cat.${opt.category}`) : t('settings.accentMono')}
              onClick={() => (locked ? setLockedTap(true) : setAccent(opt.id))}
              className={cn(
                'relative flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-all hover:scale-110 active:scale-95',
                active && 'scale-110 ring-2 ring-foreground ring-offset-2 ring-offset-background',
              )}
              style={{ backgroundColor: fill(opt.color) }}
            >
              {active && (
                <Check className="size-4 text-white dark:text-[#0a0a0a]" strokeWidth={3} />
              )}
              {locked && (
                <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/35">
                  <Lock className="size-3.5 text-white" strokeWidth={2.5} />
                </span>
              )}
            </button>
          )
        })}
      </div>
      {lockedTap && !isPro && (
        <p className="text-xs text-muted-foreground">{t('onboarding.flow.colorProHint')}</p>
      )}
    </div>
  )
}

function CoachStep({
  goalId,
  focus,
  category,
  draft,
  update,
}: {
  goalId?: string
  focus?: Focus | null
  category?: string
  draft: CoachDraft
  update: (d: CoachDraft) => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const prefs = useQuery(api.preferences.getMyPreferences) as Prefs | undefined
  const updatePrefs = useUpdatePreferences()
  const completeOnboarding = useMutation(api.preferences.completeUserOnboarding)
  const paywallVariant = usePaywallVariant()
  const setHeardFrom = useMutation(api.preferences.setHeardFrom)
  const setDayStartHour = useViewStore((s) => s.setDayStartHour)
  const planTime = useOnboardingStore((s) => s.planAnswers.time)
  const push = useWebPush()
  const { tier } = useIsPro()

  const currentUser = useQuery(api.auth.getCurrentUser)
  const { data: session } = authClient.useSession()
  const loginName = currentUser?.name || session?.user?.name || ''
  const email = currentUser?.email || session?.user?.email || ''
  // Only the first name is asked for; the account keeps the full login name.
  const name = draft.name ?? loginName.trim().split(/\s+/)[0] ?? ''
  const nameRef = useRef<HTMLInputElement>(null)
  const focusChecked = useRef(false)
  useEffect(() => {
    if (focusChecked.current || currentUser === undefined) return
    focusChecked.current = true
    // Desktop only: on touch devices focusing pops the keyboard over the coaches.
    if (!name && window.matchMedia('(hover: hover) and (pointer: fine)').matches)
      nameRef.current?.focus()
  }, [currentUser, name])

  const voice = onboardingCoachVoice(name, prefs?.coachVoice, draft.voice)
  const suggestions = onboardingCoachSuggestions(name, voice)
  // Null = adaptive (coachStyle left unset), the coach's default.
  const style = draft.style ?? null
  const [toneOpen, setToneOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(() => !FEATURED.has(voice) && !realisticCoach(voice))
  const lineKey =
    ONBOARDING_COACHES.find((c) => c.line === style)?.line ??
    ONBOARDING_COACHES.find((c) => c.voice === voice)?.line ??
    'warm'
  const nightOwl = draft.nightOwl ?? (prefs?.dayStartHour ?? 0) >= NIGHT_OWL_DAY_START_HOUR
  const remindOn = draft.remind ?? prefs?.notifStreakAlerts !== false
  const [saving, setSaving] = useState(false)
  const intro = useIntroPreview(style ?? undefined)
  const suggestionKey = suggestions.map((c) => c.id).join(',')
  useEffect(() => {
    const voices = [
      ...ONBOARDING_COACHES.map((c) => c.voice),
      ...suggestionKey
        .split(',')
        .filter(Boolean)
        .map((id) => realisticVoice(id)),
    ]
    intro.prefetch(voices)
  }, [style, suggestionKey])
  useEffect(() => {
    if (moreOpen) intro.prefetch(MORE_VOICES.map((v) => v.id))
  }, [moreOpen, style])

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const firstName = name.trim().split(/\s+/)[0] || t('onboarding.flow.friend')

  async function handleStart() {
    if (saving) return
    setSaving(true)
    intro.stop()
    const finalName = name.trim() || loginName.trim() || email.split('@')[0] || undefined
    // Untouched toggle keeps an existing Settings value (e.g. 4 or 5) instead of resetting it.
    const dayStartHour =
      draft.nightOwl === undefined && prefs?.dayStartHour !== undefined
        ? prefs.dayStartHour
        : dayStartHourFor(nightOwl)
    // Asked only now, on the Start tap. Not awaited: the prompt can sit open
    // over Today, and the pref is saved either way so it applies once granted.
    if (push.supported && remindOn && push.permission === 'default') void push.subscribe()
    setDayStartHour(dayStartHour)
    await Promise.all([
      updatePrefs({
        coachVoice: voice,
        coachAvatarStyle: realisticCoach(voice) ? 'realistic' : 'illustrated',
        ...(style && { coachStyle: style }),
        dayStartHour,
        ...(push.supported && { notifStreakAlerts: remindOn }),
      }),
      completeOnboarding({
        name: finalName,
        timeAvailable: planTime,
        lifeAreas: category ? [category.toLowerCase()] : undefined,
        ...(focus && { focus }),
      }),
      finalName && !loginName.trim() && authClient.updateUser({ name: finalName }),
      draft.heardFrom && setHeardFrom({ source: draft.heardFrom, other: draft.heardFromOther }),
    ]).catch(() => {})
    if (draft.heardFrom) {
      const heard = { heard_from: draft.heardFrom, heard_from_other: draft.heardFromOther ?? null }
      posthog.capture('heard_from_answered', { ...heard, $set_once: heard })
    }
    posthog.capture('onboarding_completed', { focus: focus ?? null, platform: 'web' })
    markOnboardingFinished(goalId ?? null)
    clearCoachStyleHandoff()
    useOnboardingStore.getState().reset()
    useOnboardingStore.getState().setFocus(null)
    if (paywallVariant === 'trial-paywall')
      navigate({
        to: '/onboarding/paywall',
        search: { next: focus === 'habits' ? 'habits' : 'today' },
        replace: true,
      })
    else if (focus === 'habits')
      navigate({ to: '/everyday', search: { tab: 'habits' }, replace: true })
    else navigate({ to: '/today', replace: true })
  }

  const card = (v: CoachVoice, className?: string) => {
    const meta = COACH_VOICES.find((c) => c.id === v)
    return (
      <CoachCard
        key={v}
        voice={v}
        label={meta?.label ?? v}
        blurb={t(`onboarding.flow.coachBlurb.${v}`)}
        selected={voice === v}
        playing={intro.playing === v}
        trialPill={tier === 'trial' && voice === v}
        // Same as the coach sheet's voice picker: tapping a coach picks it and
        // plays its intro; tapping the one speaking stops it.
        onSelect={() => {
          update({ voice: v })
          intro.toggle(v)
        }}
        className={className}
      />
    )
  }

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-lg space-y-7 px-4 pt-6 pb-48">
          <div className="space-y-1">
            <h1 className="text-2xl font-black tracking-tight">
              {t('onboarding.flow.profileTitle')}
            </h1>
            <p className="text-sm text-muted-foreground">{t('onboarding.flow.profileHint')}</p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-semibold text-foreground/70">
              {t('onboarding.flow.nameLabel')}
            </label>
            <input
              ref={nameRef}
              type="text"
              value={name}
              onChange={(e) =>
                update({
                  name: e.target.value.replace(
                    /(^|\s)(\p{L})/gu,
                    (_, sep, ch) => sep + ch.toUpperCase(),
                  ),
                })
              }
              placeholder={t('onboarding.flow.namePlaceholder')}
              autoCapitalize="words"
              autoComplete="name"
              disabled={saving}
              className="w-full rounded-2xl border border-border/60 bg-card/40 px-5 py-3.5 text-base backdrop-blur-sm transition-colors placeholder:text-muted-foreground/50 focus-visible:border-primary/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/30 disabled:opacity-50"
            />
          </div>

          <AccentPicker />

          <div className="space-y-2.5">
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-sm font-semibold text-foreground/70">
                {t('onboarding.flow.coachLabel')}
              </p>
              <button
                type="button"
                onClick={() => setMoreOpen((v) => !v)}
                aria-expanded={moreOpen}
                className="flex cursor-pointer items-center gap-0.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                {t('onboarding.flow.moreVoices', { count: MORE_VOICES.length })}
                <ChevronRight
                  className={cn('h-3.5 w-3.5 transition-transform', moreOpen && 'rotate-90')}
                />
              </button>
            </div>
            <div className={cn('grid gap-2', suggestions.length ? 'grid-cols-2' : 'grid-cols-3')}>
              {suggestions.map((c) => {
                const id = realisticVoice(c.id)
                return (
                  <CoachCard
                    key={c.id}
                    voice={id}
                    label={c.name}
                    blurb={c.blurb}
                    image={`/coach-faces/${c.id}.jpg`}
                    selected={voice === id}
                    playing={intro.playing === id}
                    trialPill={tier === 'trial' && voice === id}
                    onSelect={() => {
                      update({ voice: id })
                      intro.toggle(id)
                    }}
                  />
                )
              })}
              {ONBOARDING_COACHES.map((c) => card(c.voice))}
            </div>
            {moreOpen && (
              <div className="grid grid-cols-3 gap-2">{MORE_VOICES.map((v) => card(v.id))}</div>
            )}
            <p className="rounded-xl bg-muted/50 px-4 py-2.5 text-sm italic text-foreground/80">
              “{t(`onboarding.flow.coachLine.${lineKey}`, { name: firstName })}”
            </p>
            <p className="text-xs text-muted-foreground">
              {style
                ? t('onboarding.flow.toneLabel', { tone: t(`coach.style.${style}`) })
                : t('onboarding.flow.toneAdapts')}
              {' · '}
              <button
                type="button"
                onClick={() => setToneOpen((v) => !v)}
                className="cursor-pointer underline underline-offset-2 transition-colors hover:text-foreground"
              >
                {t('onboarding.flow.toneChange')}
              </button>
            </p>
            {toneOpen && (
              <div className="flex flex-wrap gap-2">
                {[null, ...COACH_STYLES.map((s) => s.id)].map((id) => (
                  <ChipButton
                    key={id ?? 'adaptive'}
                    label={id ? t(`coach.style.${id}`) : t('coach.adaptive')}
                    selected={style === id}
                    onClick={() => update({ style: id })}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Toggle
              icon={<Moon className="h-4 w-4" />}
              title={t('onboarding.flow.nightOwlTitle')}
              hint={t('onboarding.flow.nightOwlHint')}
              on={nightOwl}
              onClick={() => update({ nightOwl: !nightOwl })}
            />
            <p className="px-1 text-xs text-muted-foreground">
              {t('onboarding.flow.timezone', { tz: timeZone })}
            </p>
          </div>

          {push.supported && (
            <div className="space-y-1.5">
              <Toggle
                icon={<Bell className="h-4 w-4" />}
                title={t('onboarding.flow.remindTitle')}
                hint={t('onboarding.flow.remindHint')}
                on={remindOn}
                onClick={() => update({ remind: !remindOn })}
              />
              {remindOn && push.permission === 'denied' && (
                <p className="px-1 text-xs text-muted-foreground">
                  {t('onboarding.flow.remindBlocked')}
                </p>
              )}
            </div>
          )}

          <HeardFromPicker
            value={draft.heardFrom}
            other={draft.heardFromOther}
            onChange={(heardFrom, heardFromOther) => update({ heardFrom, heardFromOther })}
          />
        </div>
      </div>
      <FloatingFooter>
        <button type="button" disabled={saving} onClick={handleStart} className={primaryBtn}>
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          {t('onboarding.flow.start')}
        </button>
      </FloatingFooter>
    </div>
  )
}

function Toggle({
  icon,
  title,
  hint,
  on,
  onClick,
}: {
  icon: ReactNode
  title: string
  hint: string
  on: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onClick}
      className="flex w-full cursor-pointer items-center gap-3 rounded-2xl border border-border/60 bg-card/40 px-4 py-3.5 text-left transition-colors hover:bg-card/60"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary-text">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{title}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
      </div>
      <span
        className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${on ? 'bg-primary' : 'bg-muted-foreground/25'}`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${on ? 'translate-x-5' : 'translate-x-0'}`}
        />
      </span>
    </button>
  )
}
